import React, { useEffect, useState } from 'react';
import { X, Send, Share2 } from 'lucide-react';
import { shareLinkFor, shareDesign } from './shareDesign.jsx';
import { markShareSent } from './sharesApi.jsx';
import './shareSheet.scss';

// منوی «اشتراک»: گزینه‌ی اول پیامک (منوی اشتراکِ خودِ گوشی ترتیبش دستِ ما نیست، برای همین منوی خودمان)، بعد
// واتس‌اپ، تلگرام، کپیِ لینک و «برنامه‌های دیگر» (منوی اشتراکِ گوشی). با هر کدام لینک در «فرستاده‌ها» می‌آید.
// لینک‌ها <a> واقعی‌اند تا گوشی بی‌مشکل (همان لحظه‌ی لمس) پیامک/واتس‌اپ/تلگرام را باز کند.
// onResult('shared' | 'copied' | 'failed')

const isIOS = () => typeof navigator !== 'undefined' && /iPad|iPhone|iPod/.test(navigator.userAgent || '');

function Icon({ kind }) {
  if (kind === 'sms') {
    return (
      <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 12a8 8 0 0 1-11.6 7.1L4 20.5l1.4-4.6A8 8 0 1 1 21 12z" />
        <path d="M8.5 11.5h.01M12 11.5h.01M15.5 11.5h.01" />
      </svg>
    );
  }
  if (kind === 'wa') {
    return (
      <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
        <path d="M20.5 11.7a8.6 8.6 0 0 1-12.7 7.5L3.5 20.5l1.3-4.2a8.6 8.6 0 1 1 15.7-4.6z" />
        <path d="M9 8.5c0 3 2.5 6.5 6.5 6.5l1-1.5-2-1-1 1c-1-.5-2-1.5-2.5-2.5l1-1-1-2z" />
      </svg>
    );
  }
  if (kind === 'tg') {
    return (
      <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 4 3 11l6 2 2 6 3-4 5 4z" />
        <path d="m9 13 8-6" />
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
      <rect x="9" y="9" width="11" height="11" rx="2" />
      <path d="M5 15V6a2 2 0 0 1 2-2h9" />
    </svg>
  );
}

export default function ShareSheet({ design, onClose, onResult }) {
  const [link, setLink] = useState(null);
  useEffect(() => {
    let alive = true;
    shareLinkFor(design).then((l) => alive && setLink(l));
    return () => {
      alive = false;
    };
  }, [design]);

  const full = link ? `${link.text}\n${link.url}` : '';
  const sent = () => {
    if (link && link.code) markShareSent(link.code);
    onResult && onResult('shared');
    onClose();
  };
  const copy = async () => {
    let ok = false;
    try {
      await navigator.clipboard.writeText(full);
      ok = true;
    } catch (e) {
      const ta = document.createElement('textarea');
      ta.value = full;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      try {
        ok = document.execCommand('copy');
      } catch (e2) {
        ok = false;
      }
      ta.remove();
    }
    if (ok && link && link.code) markShareSent(link.code);
    onResult && onResult(ok ? 'copied' : 'failed');
    onClose();
  };
  const other = async () => {
    const r = await shareDesign(design);
    if (r !== 'cancelled') onResult && onResult(r);
    onClose();
  };

  const enc = encodeURIComponent;
  const sms = link ? `sms:${isIOS() ? '&' : '?'}body=${enc(full)}` : undefined;
  const wa = link ? `https://wa.me/?text=${enc(full)}` : undefined;
  const tg = link ? `https://t.me/share/url?url=${enc(link.url)}&text=${enc(link.text)}` : undefined;

  return (
    <div className="share-sheet" onClick={onClose}>
      <div className="share-sheet__box" onClick={(e) => e.stopPropagation()}>
        <div className="share-sheet__head">
          <strong>فرستادن برای دیگران</strong>
          <button type="button" onClick={onClose} aria-label="بستن">
            <X size={18} strokeWidth={2} />
          </button>
        </div>
        {!link ? (
          <p className="share-sheet__wait">در حالِ آماده کردنِ لینک…</p>
        ) : (
          <div className="share-sheet__list">
            <a href={sms} onClick={sent} className="is-sms">
              <span>
                <Icon kind="sms" />
              </span>
              پیامک
            </a>
            <a href={wa} target="_blank" rel="noreferrer" onClick={sent} className="is-wa">
              <span>
                <Icon kind="wa" />
              </span>
              واتس‌اپ
            </a>
            <a href={tg} target="_blank" rel="noreferrer" onClick={sent} className="is-tg">
              <span>
                <Icon kind="tg" />
              </span>
              تلگرام
            </a>
            <button type="button" onClick={copy}>
              <span>
                <Icon kind="copy" />
              </span>
              کپیِ لینک
            </button>
            {typeof navigator !== 'undefined' && navigator.share && (
              <button type="button" onClick={other}>
                <span>
                  <Share2 size={22} strokeWidth={1.9} />
                </span>
                بقیه‌ی برنامه‌ها
              </button>
            )}
          </div>
        )}
        <p className="share-sheet__note">
          <Send size={12} strokeWidth={2} /> هر لینکی که بفرستید در تبِ «شیر» دیده می‌شود و وقتی گیرنده بازش کرد خبردار می‌شوید.
        </p>
      </div>
    </div>
  );
}
